import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { displayNameFromLogin } from './displayNameFromLogin';
import { isEmailLikeDisplayLabel } from '../utils/headerDisplayName';
import {
  getStoredProfileDetailsHints,
  joinedProfileDetailsDisplayName,
} from '../utils/profileDetailsStorage';
import { formatPhoneForDisplay } from '../utils/phoneE164';
import {
  registerSessionExpiredHandler,
  resetSessionExpiredNotifyGate,
} from './sessionExpired';
import {
  clearPersistedSession,
  getSessionToken,
  getStoredLoginType,
  getStoredUserName,
  persistAccessTokenOnly,
  persistSession,
  persistUserNameOnly,
} from './sessionToken';
import {
  isAccessTokenValidForApi,
  resetApiTokenCache,
} from '../api/withApiAuth';
import { obtainFreshAccessViaRefresh } from '../api/authRefresh';
import { persistIdentifierForLoginOption } from '../login/lastLoginStorage';
import {
  contactHintsFromAuthBody,
  contactHintsFromJwt,
  contactHintsFromLoginIdentifier,
  mergeContactHints,
  persistProfileContactHints,
} from '../utils/profileContactStorage';
import { clearStoredBusinessClientWorkerIdentifier } from '../utils/businessClientIdentifierStorage';
import { markUserInitiatedLogout, clearUserInitiatedLogoutGate } from './logoutGate';
import { flushLocalFavoriteAssetsToServer } from '../favorites/favoriteAssetsServerPush';
import { clearLoginGracePeriod, markLoginGracePeriod } from './loginGrace';
import { clearBrowserAuthCookiesBestEffort, postAuthLogout } from './authLogout';
import { signOutNativeGoogleAuth } from '../login/nativeGoogleSignIn';

/** Log out after this long with no pointer, keyboard, or scroll activity. */
const IDLE_LOGOUT_MS = 2 * 60 * 60 * 1000;

const IDLE_ACTIVITY_THROTTLE_MS = 1000;

function initialUserName(): string | null {
  const stored = getStoredUserName();
  if (stored) {
    return stored;
  }
  const t = getSessionToken();
  if (t) {
    const d = displayNameFromLogin(null, t, '');
    return d && d !== 'User' ? d : null;
  }
  return null;
}

type AuthValue = {
  userName: string | null;
  /** Raw login menu value, e.g. `business client`, `private asset owner`. */
  userLoginType: string | null;
  isLoggedIn: boolean;
  /** False until the first session/refresh reconcile on load finishes. */
  isAuthReady: boolean;
  applyLoginSuccess: (
    body: unknown,
    accessToken: string,
    loginIdentifier: string,
    loginType: string,
  ) => void;
  /** Prefer profile name in the top bar after GET /profile or profile edit. */
  updateUserDisplayName: (displayName: string) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthValue | null>(null);

/** Max wait for refresh reconciliation on cold load — avoids an endless splash on mobile. */
const AUTH_SESSION_RECONCILE_TIMEOUT_MS = 8000;

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<null>((resolve) => {
        timeoutId = setTimeout(() => resolve(null), ms);
      }),
    ]);
  } catch {
    return null;
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
}

type AuthProviderProps = { children: ReactNode };

export function AuthProvider({ children }: AuthProviderProps) {
  const [userName, setUserName] = useState<string | null>(initialUserName);
  const [userLoginType, setUserLoginType] = useState<string | null>(() => getStoredLoginType());
  const [isAuthReady, setIsAuthReady] = useState(false);

  const applyLoginSuccess = useCallback(
    (body: unknown, accessToken: string, loginIdentifier: string, loginType: string) => {
      const name = displayNameFromLogin(body, accessToken, loginIdentifier);
      let displayName =
        name === 'Phone' && !loginIdentifier.includes('@')
          ? formatPhoneForDisplay(loginIdentifier)
          : name;
      const hintsName = joinedProfileDetailsDisplayName(getStoredProfileDetailsHints(loginIdentifier));
      if (hintsName && isEmailLikeDisplayLabel(displayName)) {
        displayName = hintsName;
      }
      persistSession(accessToken, displayName, loginType, loginIdentifier);
      persistIdentifierForLoginOption(loginType, loginIdentifier);
      const contacts = mergeContactHints(
        contactHintsFromAuthBody(body),
        contactHintsFromLoginIdentifier(loginIdentifier),
        contactHintsFromJwt(accessToken),
      );
      /**
       * Email / Google login JSON often still carries an old phone. Keep the stored phone
       * until GET /profile overwrites it — do not write the login-body number.
       */
      const phoneToPersist = loginIdentifier.includes('@')
        ? undefined
        : contacts.phone ?? loginIdentifier;
      persistProfileContactHints(contacts.email, phoneToPersist, loginIdentifier);
      resetSessionExpiredNotifyGate();
      clearUserInitiatedLogoutGate();
      markLoginGracePeriod();
      setUserName(displayName);
      setUserLoginType(loginType);
    },
    [],
  );

  const updateUserDisplayName = useCallback((displayName: string) => {
    const trimmed = displayName.trim();
    if (!trimmed) {
      return;
    }
    persistUserNameOnly(trimmed);
    setUserName(trimmed);
  }, []);

  const logout = useCallback(() => {
    markUserInitiatedLogout();
    clearLoginGracePeriod();
    void (async () => {
      try {
        await flushLocalFavoriteAssetsToServer();
      } catch {
        /* still sign out below */
      }
      clearPersistedSession();
      clearStoredBusinessClientWorkerIdentifier();
      resetApiTokenCache();
      resetSessionExpiredNotifyGate();
      clearBrowserAuthCookiesBestEffort();
      void postAuthLogout();
      void signOutNativeGoogleAuth();
      setUserName(null);
      setUserLoginType(null);
    })();
  }, []);

  useEffect(() => {
    registerSessionExpiredHandler(logout);
    return () => registerSessionExpiredHandler(null);
  }, [logout]);

  useEffect(() => {
    let cancelled = false;

    const reconcileExpiredSession = async () => {
      try {
        const session = getSessionToken();
        const loginType = getStoredLoginType();
        if (!session || !loginType) {
          return;
        }
        if (isAccessTokenValidForApi(session)) {
          return;
        }
        const fresh = await withTimeout(
          obtainFreshAccessViaRefresh(),
          AUTH_SESSION_RECONCILE_TIMEOUT_MS,
        );
        if (cancelled) {
          return;
        }
        if (fresh && isAccessTokenValidForApi(fresh)) {
          persistAccessTokenOnly(fresh);
          return;
        }
        logout();
      } finally {
        if (!cancelled) {
          setIsAuthReady(true);
        }
      }
    };

    void reconcileExpiredSession();
    return () => {
      cancelled = true;
    };
  }, [logout]);

  useEffect(() => {
    if (!getSessionToken()) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let lastThrottleAt = 0;

    const armTimer = () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(logout, IDLE_LOGOUT_MS);
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastThrottleAt < IDLE_ACTIVITY_THROTTLE_MS) {
        return;
      }
      lastThrottleAt = now;
      armTimer();
    };

    const eventOptions: AddEventListenerOptions = { capture: true, passive: true };
    const eventTypes: (keyof WindowEventMap)[] = [
      'pointerdown',
      'keydown',
      'wheel',
      'scroll',
      'touchstart',
    ];

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        onActivity();
      }
    };

    for (const type of eventTypes) {
      window.addEventListener(type, onActivity, eventOptions);
    }
    document.addEventListener('visibilitychange', onVisibility);
    armTimer();

    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      for (const type of eventTypes) {
        window.removeEventListener(type, onActivity, eventOptions);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [userName, userLoginType, logout]);

  const value = useMemo<AuthValue>(
    () => ({
      userName,
      userLoginType,
      isLoggedIn: getSessionToken() != null,
      isAuthReady,
      applyLoginSuccess,
      updateUserDisplayName,
      logout,
    }),
    [userName, userLoginType, isAuthReady, applyLoginSuccess, updateUserDisplayName, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
